import _ from 'lodash'

/** Rough bytes per full `rewards_company/{company}` download (observed ~60KB). */
export const REWARD_CATALOG_READ_BYTES_ESTIMATE = 60 * 1024

type RewardCatalogState = {
  selectedCountryFilter: string
  memberCountries: Array<{ value?: string }>
  rewardsall_db_loaded: boolean
  rewardsall_db: Record<string, any>
  tabs: string
  rewardsMaster: Record<string, string>
}

function parseRewardTimestamps(data: any, key: string): { end_time: number; current_time: number } | null {
  if (
    !data.starton || !data.endon ||
    typeof data.starton !== 'string' || typeof data.endon !== 'string' ||
    data.starton.trim() === '' || data.endon.trim() === ''
  ) {
    return null
  }

  try {
    const endParts = data.endon.split(' ')
    const endDateParts = endParts[0] ? endParts[0].split('/') : []
    const endTimeParts = endParts[1] ? endParts[1].split(':') : ['23', '59']

    if (endDateParts.length !== 3 || endTimeParts.length !== 2) {
      throw new Error('Invalid end date format')
    }

    const year = parseInt(endDateParts[2], 10)
    const month = parseInt(endDateParts[1], 10) - 1
    const day = parseInt(endDateParts[0], 10)
    const hour = parseInt(endTimeParts[0], 10)
    const minute = parseInt(endTimeParts[1], 10)

    if (
      isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute) ||
      year < 1900 || year > 3000 || month < 0 || month > 11 ||
      day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59
    ) {
      throw new Error('Invalid end date values')
    }

    return {
      end_time: new Date(year, month, day, hour, minute).getTime(),
      current_time: Date.now(),
    }
  } catch (parseError) {
    console.warn(`Date parsing failed for reward ${key}:`, parseError)
    return null
  }
}

function shouldShowRewardForCountry(data: any, state: RewardCatalogState): boolean {
  const selectedFilter = state.selectedCountryFilter
  if (selectedFilter === 'members_only') {
    const memberCountries = Array.isArray(state.memberCountries)
      ? state.memberCountries.map((c) => c && c.value).filter(Boolean)
      : []
    const rewardCountry = data.country ? data.country.toUpperCase() : ''
    return !data.country ||
      data.country === 'All Countries' ||
      memberCountries.includes(rewardCountry)
  }

  if (selectedFilter !== 'all' && selectedFilter !== 'all_countries') {
    const rewardCountry = data.country ? data.country.toUpperCase() : ''
    const filterCountry = selectedFilter.toUpperCase()
    return !data.country ||
      data.country === 'All Countries' ||
      rewardCountry === filterCountry
  }

  return true
}

function enrichRewardRow(data: any, key: string, state: RewardCatalogState, isExpired: boolean, tagValue: string) {
  const rewardCompany: any = { ...data }
  const masterRewardId = rewardCompany.master_reward_id || key

  if (rewardCompany.rewards && typeof rewardCompany.rewards === 'object') {
    rewardCompany.names = rewardCompany.rewards
  } else {
    rewardCompany.names = {
      en: rewardCompany.reward_en || '',
      th: rewardCompany.reward || '',
    }
  }

  if (rewardCompany.descriptions && typeof rewardCompany.descriptions === 'object') {
    rewardCompany.descriptions = rewardCompany.descriptions
  } else {
    rewardCompany.descriptions = {
      en: rewardCompany.description_en || '',
      th: rewardCompany.description || '',
    }
  }

  rewardCompany.key = key
  rewardCompany.coins = parseInt(rewardCompany.coins, 10) || 0
  rewardCompany.gems = parseInt(rewardCompany.gems, 10) || 0
  rewardCompany.linked_variant = !!rewardCompany.master_reward_id

  if (rewardCompany.filepath && typeof rewardCompany.filepath === 'string') {
    try {
      let path = rewardCompany.filepath
      const fstr = 'https://firebasestorage.googleapis.com/v0/b/people2-admin.appspot.com/o/'
      const lstr = '?alt=media'
      path = path.replace(/\//g, '%2F')
      rewardCompany.img = fstr + path + lstr
    } catch (error) {
      console.warn(`Error processing image path for reward ${key}:`, error)
    }
  }

  if (
    state.rewardsall_db_loaded &&
    state.rewardsall_db &&
    state.rewardsall_db[masterRewardId] == undefined
  ) {
    rewardCompany.edit = 'Y'
    try {
      const splitEndon = rewardCompany.endon.split(' ')
      const dateEndon = splitEndon[0].split('/')
      rewardCompany.endon_object = new Date(`${dateEndon[2]}/${dateEndon[1]}/${dateEndon[0]} ${splitEndon[1]}:00`)

      const splitStarton = rewardCompany.starton.split(' ')
      const dateStarton = splitStarton[0].split('/')
      rewardCompany.starton_object = new Date(`${dateStarton[2]}/${dateStarton[1]}/${dateStarton[0]} ${splitStarton[1]}:00`)
    } catch (error) {
      console.warn(`Error parsing dates for reward ${key}:`, error)
    }
  } else {
    rewardCompany.edit = 'N'
  }

  rewardCompany.block_status = rewardCompany.status != 'N'
  rewardCompany.order_status = 2
  rewardCompany.tag_value = tagValue
  rewardCompany.isExpired = isExpired

  if (masterRewardId && state.rewardsMaster && state.rewardsMaster[masterRewardId] && state.rewardsMaster[masterRewardId] == 'N') {
    return null
  }

  if (!rewardCompany.oldreward) {
    rewardCompany.order_status = 1
  }

  return rewardCompany
}

export function processRewardsFromCatalog(catalogRaw: Record<string, any> = {}, state: RewardCatalogState) {
  const countnewreward: any = {
    gift_cards: 0,
    perks: 0,
    team_building: 0,
    doing_good: 0,
    all: 0,
  }
  const newReward: any = {}
  const rewards: any[] = []

  Object.keys(catalogRaw || {}).forEach((key) => {
    try {
      const data = catalogRaw[key]
      if (!data || typeof data !== 'object') {
        return
      }

      const timestamps = parseRewardTimestamps(data, key)
      if (!timestamps) {
        return
      }

      if (!shouldShowRewardForCountry(data, state)) {
        return
      }

      const isExpired = timestamps.end_time < timestamps.current_time
      const masterRewardId = data.master_reward_id || key
      const isCustomReward = state.rewardsall_db_loaded &&
        masterRewardId != null &&
        state.rewardsall_db &&
        state.rewardsall_db[masterRewardId] == undefined

      if (isExpired && !isCustomReward) {
        return
      }

      const isVisible = data.show && data.show === 'Y'
      if (!isVisible || !data.tag || !Array.isArray(data.tag) || data.tag.length === 0) {
        return
      }

      const tagValue = _.find(data.tag, (tag) => tag == state.tabs)
      if (!tagValue) {
        return
      }

      if (masterRewardId && state.rewardsMaster && state.rewardsMaster[masterRewardId] && state.rewardsMaster[masterRewardId] == 'N') {
        return
      }

      const rewardRow = enrichRewardRow(data, key, state, isExpired, tagValue)
      if (!rewardRow) {
        return
      }

      rewards.push(rewardRow)

      if (key && state.rewardsMaster && state.rewardsMaster[key] &&
        state.rewardsMaster[key] == 'Y' && !data.oldreward) {
        countnewreward.all++

        if (_.find(data.tag, (tag) => tag == '0')) {
          countnewreward.gift_cards++
          if (!newReward.gift_card) newReward.gift_card = []
          newReward.gift_card.push(key)
        }
        if (_.find(data.tag, (tag) => tag == '1')) {
          countnewreward.team_building++
          if (!newReward.team_building) newReward.team_building = []
          newReward.team_building.push(key)
        }
        if (_.find(data.tag, (tag) => tag == '2')) {
          if (!newReward.doing_good) newReward.doing_good = []
          newReward.doing_good.push(key)
          countnewreward.doing_good++
        }
        if (_.find(data.tag, (tag) => tag == '3')) {
          countnewreward.perks++
          if (!newReward.perks) newReward.perks = []
          newReward.perks.push(key)
        }
      }
    } catch (error) {
      console.error(`Error processing reward ${key}:`, error)
    }
  })

  const counts: any = _.countBy(rewards, 'status')
  counts.all = rewards.length
  const rewardsSorted = _.orderBy(rewards, ['order_status', 'reward_en'], ['asc', 'asc'])

  return {
    rewardsSorted,
    countnewreward,
    newReward,
    counts,
  }
}

export function buildCatalogCountriesFromRaw(catalogRaw: Record<string, any> = {}) {
  const { getName } = require('country-list')
  const catalogCountrySet = new Set<string>()

  Object.keys(catalogRaw || {}).forEach((key) => {
    const data = catalogRaw[key]
    const isVisible = !data || !data.show || data.show === 'Y'
    if (data && data.country && data.country !== 'All Countries' && isVisible) {
      catalogCountrySet.add(data.country.toUpperCase())
    }
  })

  const catalogCountryOptions: Array<any> = []
  catalogCountrySet.forEach((value) => {
    try {
      const countryName = getName(value.toUpperCase())
      if (countryName) {
        catalogCountryOptions.push({ label: countryName, value })
      }
    } catch (error) {
      console.warn(`Invalid catalog country code: ${value}`, error)
    }
  })

  catalogCountryOptions.sort((a, b) => a.label.localeCompare(b.label))
  return catalogCountryOptions
}

export function applyRewardViewFilters(
  rewardsall: any[] = [],
  inputSearch = '',
  selectShow = '',
) {
  let rewards = Array.isArray(rewardsall) ? rewardsall.slice() : []

  if (selectShow && selectShow !== 'all') {
    rewards = rewards.filter((item) => item.status && item.status.match(selectShow))
  }

  if (inputSearch) {
    const re = new RegExp(inputSearch, 'gi')
    rewards = rewards.filter((item) => (
      (item.reward_en && item.reward_en.match(re)) ||
      (item.description_en && item.description_en.match(re))
    ))
  }

  return rewards
}

export function getNewRewardKeysForTab(newReward: any, tab: string) {
  if (tab == '0') return newReward && newReward.gift_card ? newReward.gift_card.slice() : []
  if (tab == '1') return newReward && newReward.team_building ? newReward.team_building.slice() : []
  if (tab == '2') return newReward && newReward.doing_good ? newReward.doing_good.slice() : []
  if (tab == '3') return newReward && newReward.perks ? newReward.perks.slice() : []
  return []
}

export type RewardUsageScenario = {
  name: string
  catalogReads: number
  description: string
}

/** Models Firebase catalog reads for common Rewards-page workflows (old vs new). */
export function estimateRewardCatalogReads(scenario: string, options: { badgeCount?: number; saveCount?: number; tabSwitches?: number } = {}) {
  const { badgeCount = 0, saveCount = 0, tabSwitches = 0 } = options

  switch (scenario) {
    case 'page_load':
      return {
        old: 3,
        new: 1,
        note: 'getCountry + getRewards + getAllrewards_company → single getRewards fetch',
      }
    case 'tab_switch':
      return {
        old: badgeCount + 1,
        new: 0,
        note: 'setOldReward called getRewards per badge + handleClick; now in-memory reprocess only',
      }
    case 'country_filter':
      return { old: 1, new: 0, note: 'Filter applied from cached catalog' }
    case 'clear_search':
      return { old: 1, new: 0, note: 'searchRewards/selectStatus used to reload entire catalog' }
    case 'save_reward':
      return { old: saveCount, new: 0, note: 'Each save triggered full catalog reload' }
    case 'active_editing_session':
      return {
        old: 3 + (tabSwitches * (badgeCount + 1)) + saveCount,
        new: 1 + (tabSwitches > 0 || saveCount > 0 ? 0 : 0),
        note: '1 initial load; edits/tabs/filters stay in memory',
      }
    default:
      return { old: 0, new: 0, note: 'unknown scenario' }
  }
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

export function estimateDataTransfer(readCount: number, bytesPerRead = REWARD_CATALOG_READ_BYTES_ESTIMATE) {
  return readCount * bytesPerRead
}
